@let reseller = licensesData()?.reseller;
@let isFirstPurchase = licensesData()?.isFirstPurchase;
@let minCommitmentValue = minCommitment() ? minCommitment() : (balance | async)?.minCommitment;
@let isBuyLicensesAllow = (permissionFlags | async).isBuyLicensesAllow;
@let isLoadingDiscountedPrice = loadingDiscountedPrice();
@let postPaymentValue = isPostPayment();
@let renewalDateValue = totalRenewalDate();
@let isPaymentPeriodSelectable = isFirstPurchase && !reseller && isBuyLicensesAllow;

<mbs-modal
  #modal
  [bodyClasses]="'modal-body-content-wrapper'"
  [isCustomModal]="true"
  [loading]="loading()"
  [showFooter]="true"
  [title]="title()"
  [titleTextOverflow]="true">
  <div
    class="modal-body-content"
    modal-body>
    @if (postPaymentValue) {
      <mbs-alert
        class="mb-4"
        [attr.data-e2e]="elementsSelector.name.postPaymentInfoAlert"
        [icon]="true"
        [type]="mbsPopupType.info">
        {{ 'shared:licenses.buyLicenseModal.postPaymentInfo' | i18next }}
      </mbs-alert>
    }

    @if (reseller) {
      <div class="bg-blue-15 rounded-4 mb-4 p-3">
        <div class="row">
          <div class="col-auto">
            <div class="ico ico-Briefcase reseller-icon d-flex align-items-center justify-content-center"></div>
          </div>
          <div class="col">
            <div class="d-flex align-items-center h-100">
              <div>
                <div class="fw-bold">{{ 'shared:licenses.buyLicenseModal.reseller' | i18next }}</div>
                <div>
                  @if (reseller.name) {
                    <span>{{ reseller.name }} </span>
                    <span class="text-gray-600 mx-2">|</span>
                  }
                  <a href="mailto:{{ reseller.email }}">{{ reseller.email }}</a>
                  @if (reseller.phone) {
                    <span class="text-gray-600 mx-2">|</span>
                    <a href="tel:{{ reseller.phone }}">{{ reseller.phone }}</a>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    }

    @if (isPaymentPeriodSelectable || renewalDateValue) {
      <div class="d-flex align-items-center justify-content-end mb-3">
        @if (isPaymentPeriodSelectable) {
          <mbs-group-button>
            <mbs-button
              [active]="isMonthlyPayment()"
              (click)="setPaymentPeriod(true)">
              {{ 'shared:licenses.buyLicenseModal.monthly' | i18next }}
            </mbs-button>

            <mbs-button
              [active]="!isMonthlyPayment()"
              (click)="setPaymentPeriod(false)">
              {{ 'shared:licenses.buyLicenseModal.annual' | i18next }}
            </mbs-button>
          </mbs-group-button>
        } @else {
          @let formattedRenewalDate = renewalDateValue | date: 'mediumDate';
          <span class="fw-semibold">
            {{ 'shared:licenses.buyLicenseModal.renewalDate' | i18next: { date: formattedRenewalDate } }}
          </span>
        }
      </div>
    }

    <div class="prices-table">
      @if (licensesData()) {
        <mbs-licenses-prices-table
          [isBuyLicensesAllow]="isBuyLicensesAllow"
          [isDiscountedPriceShown]="isDiscountedPriceShown()"
          [isMonthlyPayment]="isMonthlyPayment()"
          [licensesCountForm]="licensesCountForm"
          [licenseTypes]="licensesData().licenseTypesPrices"></mbs-licenses-prices-table>
      }
    </div>

    @if (isBuyLicensesAllow) {
      <div class="mt-3">
        <div class="d-flex justify-content-between mt-3">
          <div class="col-8">
            @if (isLoadingDiscountedPrice) {
              <mbs-button
                [class]="'pl-2'"
                [isCtrl]="true"
                [loading]="true" />
            } @else {
              <ol class="pl-3 text-sm">
                @if (!postPaymentValue) {
                  <li>
                    {{ 'shared:licenses.buyLicenseModal.checkLicensesNote.prefix' | i18next }}
                    <a [routerLink]="'AP/Licenses'">{{ 'shared:licenses.buyLicenseModal.checkLicensesNote.link' | i18next }}</a>
                    {{ 'shared:licenses.buyLicenseModal.checkLicensesNote.suffix' | i18next }}
                  </li>
                  <li [innerHTML]="'shared:licenses.buyLicenseModal.autoRenewedNote' | i18next"></li>
                  <li>{{ 'shared:licenses.buyLicenseModal.paymentDetailsNote' | i18next }}</li>
                }

                @let minCommitmentPrice = minCommitmentValue | pricePrepayment | async;
                <li
                  [innerHTML]="
                    (isMonthlyPayment()
                      ? 'shared:licenses.buyLicenseModal.minMonthlyPayment'
                      : 'shared:licenses.buyLicenseModal.minAnnualPayment'
                    ) | i18next: { price: minCommitmentPrice }
                  "></li>
                @if (isDiscountSummaryShown() && discountExclusionParams().licenseTypes) {
                  <li>{{ 'shared:licenses.buyLicenseModal.discountNotApplied' | i18next: discountExclusionParams() }}</li>
                }
              </ol>
            }
          </div>
          <div class="col-auto px-2">
            <div class="row justify-content-between">
              <div class="col-auto">{{ 'shared:licenses.buyLicenseModal.totalListPrice' | i18next }}</div>
              <div class="col-auto">
                {{ totalListPrice() | pricePrepayment | async }}
                <i
                  class="ico ico-info-circle text-sm text-info"
                  container="body"
                  [ngbTooltip]="'shared:licenses.buyLicenseModal.listPriceHint' | i18next">
                </i>
              </div>
            </div>
            <div class="row justify-content-between mt-2">
              <ng-container *ngTemplateOutlet="isProratedPriceFinal() ? totalDiscountedPriceTmp : totalProratedPriceTmp"></ng-container>
            </div>
            @if (isDiscountSummaryShown()) {
              <div class="row justify-content-between mt-2">
                <div class="col-auto">{{ 'shared:licenses.buyLicenseModal.discount' | i18next }}</div>
                <div class="col-auto">{{ totalDiscount() | pricePrepayment | async }}</div>
              </div>
              <div class="row justify-content-between mt-2">
                <div class="col-auto">{{ 'shared:licenses.buyLicenseModal.discountPercent' | i18next }}</div>
                <div class="col-auto">{{ totalDiscountPercent() | number: '1.0-2' }}%</div>
              </div>
            }
            <div class="row justify-content-between mt-2 fw-bold">
              <ng-container *ngTemplateOutlet="isProratedPriceFinal() ? totalProratedPriceTmp : totalDiscountedPriceTmp"></ng-container>
            </div>
          </div>
        </div>
      </div>
    }
  </div>

  <ng-container modalFooter>
    <div class="form-row w-100 justify-content-between align-items-center">
      <div class="col-auto">
        @if (!reseller) {
          <span [innerHTML]="'shared:licenses.buyLicenseModal.contactUs' | i18next"></span>
        }
      </div>

      <div class="form-row">
        @if (!postPaymentValue) {
          @if (!isBuyLicensesAllow) {
            <mbs-button
              class="col"
              data-dismiss="modal"
              type="primary"
              [loading]="buyLoading()"
              (click)="requestQuote()">
              {{ 'shared:licenses.buyLicenseModal.requestQuote' | i18next }}
            </mbs-button>
          } @else {
            <mbs-button
              class="col"
              data-dismiss="modal"
              type="primary"
              [disabled]="!isBuyAvailable()"
              [loading]="buyLoading()"
              (click)="buy()">
              {{ 'shared:buttons.buy' | i18next: { format: 'title' } }}
            </mbs-button>
          }
        }

        <mbs-button
          class="col"
          data-dismiss="modal"
          type="secondary"
          (click)="modal.close()">
          {{ 'shared:buttons.close' | i18next: { format: 'title' } }}
        </mbs-button>
      </div>
    </div>
  </ng-container>
</mbs-modal>

<ng-template #totalProratedPriceTmp>
  <div class="col-auto">{{ 'shared:licenses.buyLicenseModal.totalProratedPrice' | i18next }}</div>
  <div class="col-auto position-relative">
    {{ totalProratedPrice() | pricePrepayment | async }}
    <i
      class="ico ico-info-circle text-sm text-info"
      container="body"
      [ngbTooltip]="'shared:licenses.buyLicenseModal.proratedPriceHint' | i18next">
    </i>
  </div>
</ng-template>

<ng-template #totalDiscountedPriceTmp>
  <div class="col-auto">{{ 'shared:licenses.buyLicenseModal.totalDiscountedPrice' | i18next }}</div>
  <div class="col-auto">
    {{ totalDiscountedPrice() | pricePrepayment | async }}
    <i
      class="ico ico-info-circle text-sm text-info"
      container="body"
      [ngbTooltip]="'shared:licenses.buyLicenseModal.discountPriceHint' | i18next">
    </i>
  </div>
</ng-template>
